import {
  getViewportForBounds,
  useReactFlow,
  useStoreApi,
  type Node,
} from "@xyflow/react";
import isEqual from "lodash/isEqual";
import { useEffect, useRef } from "react";
import type { CanvasReplayFrame } from "../context/CanvasReplayContext";

const PADDING = 0.3;
const MAX_ZOOM = 1;
/** Fit with whatever is measured if a node never reports a size. */
const MEASURE_TIMEOUT_MS = 1_000;

/**
 * Nodes `next` adds or changes relative to `prev`, plus both ends of every
 * edge it adds or changes. Deleted nodes are left out: nothing to look at.
 */
export function changedNodeIds(
  prev: CanvasReplayFrame,
  next: CanvasReplayFrame,
): string[] {
  const before = new Map(prev.nodes.map((n) => [n.id, n]));
  const ids = new Set<string>();
  for (const node of next.nodes) {
    const old = before.get(node.id);
    if (
      !old ||
      old.position?.x !== node.position?.x ||
      old.position?.y !== node.position?.y ||
      old.width !== node.width ||
      old.height !== node.height ||
      old.parentId !== node.parentId ||
      !isEqual(old.data, node.data)
    ) {
      ids.add(node.id);
    }
  }
  const edgesBefore = new Map(prev.edges.map((e) => [e.id, e]));
  const present = new Set(next.nodes.map((n) => n.id));
  for (const edge of next.edges) {
    if (isEqual(edgesBefore.get(edge.id), edge)) continue;
    for (const id of [edge.source, edge.target]) {
      if (present.has(id)) ids.add(id);
    }
  }
  return [...ids];
}

/**
 * Moves the camera to what changed whenever the shown frame changes. The diff
 * runs against the last frame it saw, so jumps work too; while `holding`
 * (slider drag) frames pass without moving the camera or the baseline, and
 * release diffs the whole drag at once.
 */
export function useReplayFollow({
  frame,
  follow,
  holding,
  durationMs,
}: {
  frame: CanvasReplayFrame | undefined;
  follow: boolean;
  holding: boolean;
  durationMs: number;
}) {
  const store = useStoreApi<Node>();
  const { getNodesBounds, setViewport } = useReactFlow<Node>();
  const baselineRef = useRef<CanvasReplayFrame | undefined>(undefined);
  // Read at frame change: toggling Follow or speed must not cancel a move
  // already waiting on measurement.
  const followRef = useRef(follow);
  followRef.current = follow;
  const durationRef = useRef(durationMs);
  durationRef.current = durationMs;

  useEffect(() => {
    if (!frame || holding) return;
    const prev = baselineRef.current;
    baselineRef.current = frame;
    if (!prev || prev === frame || !followRef.current) return;
    const ids = changedNodeIds(prev, frame);
    if (ids.length === 0) return;

    const wanted = new Map(
      frame.nodes.filter((n) => ids.includes(n.id)).map((n) => [n.id, n]),
    );
    // React Flow takes the frame a render later and measures it after that;
    // fit only once every changed node is in its lookup at the frame's
    // geometry and has a size.
    const ready = () => {
      const { nodeLookup } = store.getState();
      return [...wanted.values()].every((want) => {
        const node = nodeLookup.get(want.id);
        return (
          !!node?.measured?.width &&
          !!node.measured.height &&
          node.position.x === want.position.x &&
          node.position.y === want.position.y &&
          (want.width === undefined || node.width === want.width) &&
          (want.height === undefined || node.height === want.height)
        );
      });
    };

    let done = false;
    const move = () => {
      if (done) return;
      done = true;
      unsubscribe();
      window.clearTimeout(timer);
      const { nodeLookup, width, height, minZoom, maxZoom } = store.getState();
      const present = ids.filter((id) => nodeLookup.get(id)?.measured?.width);
      if (present.length === 0) return;
      const viewport = getViewportForBounds(
        getNodesBounds(present),
        width,
        height,
        minZoom,
        Math.min(maxZoom, MAX_ZOOM),
        PADDING,
      );
      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      void setViewport(viewport, {
        duration: reduceMotion ? 0 : durationRef.current,
      });
    };
    const unsubscribe = store.subscribe(() => {
      if (ready()) move();
    });
    const timer = window.setTimeout(move, MEASURE_TIMEOUT_MS);
    if (ready()) move();
    return () => {
      done = true;
      unsubscribe();
      window.clearTimeout(timer);
    };
  }, [frame, holding, store, getNodesBounds, setViewport]);
}
